import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import MKInput from "components/MKInput";
import MKAlert from "components/MKAlert";

// API
import { fetchPricing, adminUpdatePricing } from "api";

function AdminPricing({ token, onAuthError }) {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  // New date override form
  const [newOverride, setNewOverride] = useState({
    start: "",
    end: "",
    rate: "",
    reason: "",
  });

  const loadPricing = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPricing();
      setPricing(data);
    } catch (err) {
      setError(err.message || "Failed to load pricing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const { pricing: updated } = await adminUpdatePricing(token, pricing);
      setPricing(updated);
      setSuccessMsg("Pricing saved successfully");
    } catch (err) {
      if (err.message?.includes("Unauthorized")) {
        onAuthError();
        return;
      }
      setError(err.message || "Failed to save pricing");
    } finally {
      setSaving(false);
    }
  }, [token, pricing, onAuthError]);

  const updateField = (field, value) => {
    setPricing((prev) => ({ ...prev, [field]: value }));
  };

  const updateSeasonalRate = (index, field, value) => {
    setPricing((prev) => {
      const rates = [...prev.seasonalRates];
      rates[index] = { ...rates[index], [field]: value };
      return { ...prev, seasonalRates: rates };
    });
  };

  const removeSeasonalRate = (index) => {
    setPricing((prev) => ({
      ...prev,
      seasonalRates: prev.seasonalRates.filter((_, i) => i !== index),
    }));
  };

  const addSeasonalRate = () => {
    setPricing((prev) => ({
      ...prev,
      seasonalRates: [
        ...prev.seasonalRates,
        { name: "", startMonth: 1, endMonth: 1, rate: prev.baseNightlyRate },
      ],
    }));
  };

  const addDateOverride = () => {
    if (!newOverride.start || !newOverride.end || !newOverride.rate) return;
    setPricing((prev) => ({
      ...prev,
      dateOverrides: [
        ...(prev.dateOverrides || []),
        {
          start: newOverride.start,
          end: newOverride.end,
          rate: Number(newOverride.rate),
          reason: newOverride.reason || "Custom rate",
        },
      ],
    }));
    setNewOverride({ start: "", end: "", rate: "", reason: "" });
  };

  const removeDateOverride = (index) => {
    setPricing((prev) => ({
      ...prev,
      dateOverrides: prev.dateOverrides.filter((_, i) => i !== index),
    }));
  };

  const updateDateOverride = (index, field, value) => {
    setPricing((prev) => {
      const overrides = [...prev.dateOverrides];
      overrides[index] = { ...overrides[index], [field]: value };
      return { ...prev, dateOverrides: overrides };
    });
  };

  if (loading) {
    return (
      <MKBox py={6} textAlign="center">
        <CircularProgress />
        <MKTypography variant="body2" fontWeight="bold" color="dark" mt={2}>
          Loading pricing...
        </MKTypography>
      </MKBox>
    );
  }

  if (!pricing) {
    return <MKAlert color="error">Failed to load pricing configuration.</MKAlert>;
  }

  return (
    <MKBox py={2}>
      {successMsg && (
        <MKAlert color="success" dismissible sx={{ mb: 2 }}>
          {successMsg}
        </MKAlert>
      )}
      {error && (
        <MKAlert color="error" dismissible sx={{ mb: 2 }}>
          {error}
        </MKAlert>
      )}

      {/* Base settings */}
      <MKTypography variant="h5" mb={2}>
        <Icon sx={{ mr: 1, verticalAlign: "middle" }}>attach_money</Icon>
        Base Settings
      </MKTypography>
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <MKInput
            type="number"
            label="Base Nightly Rate ($)"
            fullWidth
            value={pricing.baseNightlyRate}
            onChange={(e) => updateField("baseNightlyRate", Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MKInput
            type="number"
            label="Cleaning Fee ($)"
            fullWidth
            value={pricing.cleaningFee}
            onChange={(e) => updateField("cleaningFee", Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MKInput
            type="number"
            label="VAT (%)"
            fullWidth
            value={Math.round((pricing.vatPercent || 0.13) * 100)}
            onChange={(e) => updateField("vatPercent", Number(e.target.value) / 100)}
            inputProps={{ step: 1 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MKInput
            type="number"
            label="Minimum Nights"
            fullWidth
            value={pricing.minimumNights}
            onChange={(e) => updateField("minimumNights", Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MKInput
            type="number"
            label="Max Guests"
            fullWidth
            value={pricing.maxGuests}
            onChange={(e) => updateField("maxGuests", Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MKInput
            type="number"
            label="Deposit (%)"
            fullWidth
            value={Math.round((pricing.minimumDepositPercent || 0.5) * 100)}
            onChange={(e) => updateField("minimumDepositPercent", Number(e.target.value) / 100)}
            inputProps={{ step: 5 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MKInput
            type="number"
            label="Cash Discount (%)"
            fullWidth
            value={Math.round((pricing.cashDiscountPercent || 0.06) * 100)}
            onChange={(e) => updateField("cashDiscountPercent", Number(e.target.value) / 100)}
            inputProps={{ step: 1 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MKInput
            type="number"
            label="Extra Guest Threshold"
            fullWidth
            value={pricing.extraGuestThreshold || 12}
            onChange={(e) => updateField("extraGuestThreshold", Number(e.target.value))}
            inputProps={{ min: 1 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MKInput
            type="number"
            label="Extra Guest Fee ($/person/night)"
            fullWidth
            value={pricing.extraGuestFee || 0}
            onChange={(e) => updateField("extraGuestFee", Number(e.target.value))}
            inputProps={{ min: 0 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Seasonal rates */}
      <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MKTypography variant="h5">
          <Icon sx={{ mr: 1, verticalAlign: "middle" }}>date_range</Icon>
          Seasonal Rates
        </MKTypography>
        <MKButton variant="text" color="info" size="small" onClick={addSeasonalRate}>
          <Icon sx={{ mr: 0.5 }}>add</Icon>
          Add Season
        </MKButton>
      </MKBox>
      {pricing.seasonalRates?.map((rate, i) => (
        <MKBox key={i} mb={2} p={2} bgColor="grey-100" borderRadius="lg">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <MKInput
                label="Season Name"
                fullWidth
                value={rate.name}
                onChange={(e) => updateSeasonalRate(i, "name", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <MKInput
                type="number"
                label="Start Month"
                fullWidth
                value={rate.startMonth}
                onChange={(e) => updateSeasonalRate(i, "startMonth", Number(e.target.value))}
                inputProps={{ min: 1, max: 12 }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <MKInput
                type="number"
                label="End Month"
                fullWidth
                value={rate.endMonth}
                onChange={(e) => updateSeasonalRate(i, "endMonth", Number(e.target.value))}
                inputProps={{ min: 1, max: 12 }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <MKInput
                type="number"
                label="Rate ($/night)"
                fullWidth
                value={rate.rate}
                onChange={(e) => updateSeasonalRate(i, "rate", Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6} sm={1}>
              <IconButton color="error" onClick={() => removeSeasonalRate(i)}>
                <Icon>delete</Icon>
              </IconButton>
            </Grid>
          </Grid>
        </MKBox>
      ))}

      <Divider sx={{ my: 3 }} />

      {/* Date overrides */}
      <MKTypography variant="h5" mb={1}>
        <Icon sx={{ mr: 1, verticalAlign: "middle" }}>edit_calendar</Icon>
        Date-Specific Rate Overrides
      </MKTypography>
      <MKTypography variant="body2" fontWeight="bold" color="dark" mb={2}>
        Override nightly rates for specific date ranges (e.g., holidays, special events).
      </MKTypography>

      {pricing.dateOverrides?.map((override, i) => (
        <MKBox key={i} mb={1} p={2} bgColor="grey-100" borderRadius="lg">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <MKInput
                type="date"
                label="Start Date"
                fullWidth
                value={override.start}
                onChange={(e) => updateDateOverride(i, "start", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <MKInput
                type="date"
                label="End Date"
                fullWidth
                value={override.end}
                onChange={(e) => updateDateOverride(i, "end", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <MKInput
                type="number"
                label="Rate ($/night)"
                fullWidth
                value={override.rate}
                onChange={(e) => updateDateOverride(i, "rate", Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MKInput
                label="Reason"
                fullWidth
                value={override.reason || ""}
                onChange={(e) => updateDateOverride(i, "reason", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <IconButton color="error" onClick={() => removeDateOverride(i)}>
                <Icon>delete</Icon>
              </IconButton>
            </Grid>
          </Grid>
        </MKBox>
      ))}

      <MKBox mt={2} p={2} border="1px dashed" borderColor="grey.400" borderRadius="lg">
        <MKTypography variant="button" mb={1.5} display="block">
          Add New Override
        </MKTypography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <MKInput
              type="date"
              label="Start Date"
              fullWidth
              value={newOverride.start}
              onChange={(e) => setNewOverride((p) => ({ ...p, start: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <MKInput
              type="date"
              label="End Date"
              fullWidth
              value={newOverride.end}
              onChange={(e) => setNewOverride((p) => ({ ...p, end: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <MKInput
              type="number"
              label="Rate ($/night)"
              fullWidth
              value={newOverride.rate}
              onChange={(e) => setNewOverride((p) => ({ ...p, rate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <MKInput
              label="Reason"
              fullWidth
              value={newOverride.reason}
              onChange={(e) => setNewOverride((p) => ({ ...p, reason: e.target.value }))}
              placeholder="e.g., Christmas"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <MKButton
              variant="outlined"
              color="info"
              size="small"
              fullWidth
              onClick={addDateOverride}
              disabled={!newOverride.start || !newOverride.end || !newOverride.rate}
            >
              <Icon sx={{ mr: 0.5 }}>add</Icon>
              Add
            </MKButton>
          </Grid>
        </Grid>
      </MKBox>

      <Divider sx={{ my: 3 }} />

      {/* Save button */}
      <MKBox display="flex" justifyContent="flex-end">
        <MKButton
          variant="gradient"
          color="info"
          size="large"
          onClick={handleSave}
          disabled={saving}
        >
          <Icon sx={{ mr: 0.5 }}>save</Icon>
          {saving ? "Saving..." : "Save All Changes"}
        </MKButton>
      </MKBox>
    </MKBox>
  );
}

AdminPricing.propTypes = {
  token: PropTypes.string.isRequired,
  onAuthError: PropTypes.func.isRequired,
};

export default AdminPricing;
